'use client';

import { useId, useState, type CSSProperties, type ReactNode } from 'react';

export interface TabsClientProps {
  headers: string[];
  /** One rendered panel per header, in the same order. */
  panels: ReactNode[];
  /** PageBuilder's data-active-tab (0-based). */
  defaultIndex?: number;
  /** left | center | right, from the `tab-align-*` class on the tabs element. */
  align?: 'left' | 'center' | 'right';
  className?: string;
  style?: CSSProperties;
  dataAttrs?: Record<string, string>;
  /** data-* of the `.tabs-content` wrapper, so its authored min-height/border applies. */
  contentDataAttrs?: Record<string, string>;
  contentClassName?: string;
}

/**
 * Client half of the PageBuilder "tabs" content type. Registration has to happen
 * in a server module (see content-types/slider.tsx for why), so the server
 * component collects headers and pre-renders each panel, and this component owns
 * only the selection state.
 *
 * Unregistered, a tabs block renders every panel stacked on top of each other
 * plus a stray list of `#anchor` links — which is what PageBuilder's fallback
 * markup degrades to.
 */
export function TabsClient({
  headers,
  panels,
  defaultIndex = 0,
  align = 'left',
  className,
  style,
  dataAttrs,
  contentDataAttrs,
  contentClassName,
}: TabsClientProps) {
  const base = useId();
  const start = defaultIndex >= 0 && defaultIndex < headers.length ? defaultIndex : 0;
  const [selected, setSelected] = useState(start);

  const justify =
    align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

  return (
    <div className={className} {...dataAttrs} style={style}>
      {/* The canvas has no tabs, so these are drawn in the redesign's own
          language rather than copied: a hairline strip with the active tab
          carrying a 2px accent underline, the same open/closed affordance the
          product page's accordion and the section headings use. The previous
          treatment was the old design's boxed folder tabs. */}
      <ul
        role="tablist"
        className={`flex ${justify} flex-wrap gap-6 overflow-x-auto border-b border-border`}
      >
        {headers.map((header, i) => (
          <li key={i} role="presentation" className="list-none">
            <button
              type="button"
              role="tab"
              id={`${base}-tab-${i}`}
              aria-selected={selected === i}
              aria-controls={`${base}-panel-${i}`}
              tabIndex={selected === i ? 0 : -1}
              onClick={() => setSelected(i)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') setSelected((s) => (s + 1) % headers.length);
                if (e.key === 'ArrowLeft')
                  setSelected((s) => (s - 1 + headers.length) % headers.length);
              }}
              className={`-mb-px whitespace-nowrap border-b-2 px-0.5 pb-3 pt-1 text-sm font-semibold transition-colors ${
                selected === i
                  ? 'border-accent-600 text-foreground'
                  : 'border-transparent text-muted hover:text-foreground'
              }`}
            >
              {header}
            </button>
          </li>
        ))}
      </ul>

      {/* No border or radius on the panel: the strip's own rule already separates
          it, and a box around the content re-created the folder-tab look. */}
      <div className={`relative pt-5 ${contentClassName ?? ''}`} {...contentDataAttrs}>
        {panels.map((panel, i) => (
          <div
            key={i}
            role="tabpanel"
            id={`${base}-panel-${i}`}
            aria-labelledby={`${base}-tab-${i}`}
            hidden={selected !== i}
            // min-height:inherit lets the authored min-height on .tabs-content
            // govern the panel, matching Magento's own tab sizing.
            style={{ minHeight: 'inherit' }}
          >
            {panel}
          </div>
        ))}
      </div>
    </div>
  );
}
